/**
 * The two message catalogues.
 *
 * German is the source of truth - it is what the UI was written in. English is
 * typed as `Record<MsgKey, string>`, so a key added to `de` and forgotten in
 * `en` is a compile error rather than a blank spot on the screen.
 *
 * Placeholders are `{name}`; see `t()` in ./t.ts.
 *
 * Not translated on purpose: everything the backend sends. Product names and
 * descriptions, price and pay notes, contract document names, API error
 * messages, signer role labels and the activation summary rows all arrive in
 * German and stay German in both languages.
 */

export const de = {
  // Shared verbs and nouns used in more than one place.
  "common.back": "Zurück",
  "common.next": "Weiter",
  "common.close": "Schließen",
  "common.missing": "fehlt",

  "app.loading": "Anwendung wird geladen …",
  "app.footerBrand": "AI2Connect Vertriebsplattform",

  "lang.label": "Sprache",
  "lang.de": "DE",
  "lang.en": "EN",

  "topbar.subtitle": "Vertriebsplattform",
  "topbar.nav": "Hauptnavigation",
  "topbar.tab.overview": "Übersicht",
  "topbar.tab.deal": "Vorgang",
  "topbar.demo1": "Produkt beim Kunden 1",
  "topbar.demo2": "Produkt beim Kunden 2",
  "topbar.newTab": " (öffnet in neuem Tab)",
  "topbar.logout": "Abmelden",

  "auth.shellNote": "AI2Connect Vertriebsplattform · Zugang über Ihr Firmenkonto",

  "role.vertrieb": "Vertrieb",
  "role.vertriebspartner": "Vertriebspartner",
  "role.geschaeftsfuehrung": "Geschäftsführung",

  "field.company_name": "Firma",
  "field.street": "Straße",
  "field.postal_city": "PLZ und Ort",
  "field.postal_code": "PLZ",
  "field.city": "Ort",
  "field.contact_name": "Ansprechpartner",
  "field.contact_email": "E-Mail",
  "field.missingSentence": "Es fehlt: {fields}.",

  "status.done": "Abgeschlossen",
  "status.sig": "In Signatur",
  "status.open": "In Bearbeitung",

  // --- Dashboard ---------------------------------------------------------
  "dashboard.greeting": "Guten Tag, {name}",
  "dashboard.intro": "Alle Vorgänge, die über die Plattform geführt wurden.",
  "dashboard.newDeal": "Neuen Vorgang starten",
  "dashboard.createFailed": "Der Vorgang konnte nicht angelegt werden: {message}",
  "dashboard.filter.all": "Alle",
  "dashboard.filter.internal": "AI2Connect",
  "dashboard.filter.agency": "Vertriebsagentur",
  "dashboard.filter.mine": "Nur meine",
  "dashboard.filter.open": "In Bearbeitung",
  "dashboard.filter.done": "Abgeschlossen",
  "dashboard.kpi.total": "Vorgänge gesamt",
  "dashboard.kpi.totalHint": "{count} davon von Ihnen",
  "dashboard.kpi.open": "In Bearbeitung",
  "dashboard.kpi.openHint": "noch nicht abgeschlossen",
  "dashboard.kpi.completed": "Abgeschlossen",
  "dashboard.kpi.completedHint": "Vertrag signiert und bezahlt",
  "dashboard.kpi.volume": "Abgeschlossenes Volumen",
  "dashboard.kpi.volumeHint": "brutto, nur bezahlte Vorgänge",
  "dashboard.search": "Vorgänge durchsuchen",
  "dashboard.searchPlaceholder": "Kunde, Produkt oder Vorgangsnummer suchen",
  "dashboard.col.reference": "Vorgang",
  "dashboard.col.customer": "Kunde",
  "dashboard.col.product": "Produkt",
  "dashboard.col.owner": "Bearbeiter",
  "dashboard.col.created": "Erstellt",
  "dashboard.col.value": "Wert",
  "dashboard.col.status": "Status",
  "dashboard.loading": "Vorgänge werden geladen …",
  "dashboard.empty": "Keine Vorgänge gefunden. Passen Sie Suche oder Filter an.",

  // --- Login -------------------------------------------------------------
  "login.signin.title": "Anmelden",
  "login.signin.intro":
    "Jeder Vorgang wird mit Ihrem Namen erfasst, damit nachvollziehbar bleibt, wer ihn geführt hat.",
  "login.signin.submit": "Anmelden",
  "login.signup.title": "Registrieren",
  "login.signup.intro":
    "Registrierung mit Ihrer AI2Connect-Adresse. Sie erhalten eine E-Mail mit einem Link, über den Sie Ihr Passwort festlegen.",
  "login.signup.submit": "Registrierung anfordern",
  "login.forgot.title": "Passwort vergessen",
  "login.forgot.intro":
    "Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Link, über den Sie ein neues Passwort festlegen können.",
  "login.forgot.submit": "Link anfordern",
  "login.error.email": "Bitte geben Sie Ihre E-Mail-Adresse ein.",
  "login.error.password": "Bitte geben Sie Ihr Passwort ein.",
  "login.error.name": "Bitte geben Sie Ihren Namen ein.",
  "login.error.generic": "Das hat nicht funktioniert. Bitte später erneut versuchen.",
  "login.sentHint": "Der Link ist nur einmal verwendbar. Prüfen Sie auch den Spam-Ordner.",
  "login.backToSignin": "Zurück zur Anmeldung",
  "login.field.name": "Vor- und Nachname",
  "login.field.role": "Rolle",
  "login.field.email": "E-Mail",
  "login.field.password": "Passwort",
  "login.domainHint": "Nur Adressen auf {domains} sind zugelassen.",
  "login.partnerHint":
    "Mit einer Adresse auf {domains} können Sie sich als Vertrieb registrieren. Jede andere Adresse wird als Vertriebspartner geführt.",
  "login.forcedPartner": "Adressen außerhalb von {domains} werden als Vertriebspartner geführt.",
  "login.domainOr": " oder ",
  "login.forgotLink": "Passwort vergessen?",

  // --- Set password ------------------------------------------------------
  "setPassword.tooShort": "Das Passwort muss mindestens {count} Zeichen haben.",
  "setPassword.mismatch": "Die Passwörter stimmen nicht überein.",
  "setPassword.failed":
    "Das Passwort konnte nicht gesetzt werden. Bitte später erneut versuchen.",
  "setPassword.incompleteTitle": "Link unvollständig",
  "setPassword.incompleteBody":
    "In der Adresse fehlt der Token. Bitte öffnen Sie den Link aus der E-Mail unverändert.",
  "setPassword.checking": "Link wird geprüft …",
  "setPassword.invalidTitle": "Link ungültig",
  "setPassword.invalidBody":
    "Dieser Link ist ungültig, abgelaufen oder wurde bereits verwendet. Fordern Sie über „Passwort vergessen“ einen neuen an.",
  "setPassword.resetTitle": "Neues Passwort",
  "setPassword.setTitle": "Passwort festlegen",
  "setPassword.resetIntro": "Legen Sie ein neues Passwort für Ihr Konto fest.",
  "setPassword.setIntro":
    "Willkommen bei AI2Connect. Legen Sie Ihr Passwort fest, um den Zugang zu aktivieren.",
  "setPassword.account": "Konto",
  "setPassword.field.new": "Neues Passwort",
  "setPassword.field.confirm": "Passwort bestätigen",
  "setPassword.minHint": "Mindestens {count} Zeichen.",
  "setPassword.submit": "Passwort speichern und anmelden",

  // --- Error boundary ----------------------------------------------------
  "error.title": "Es ist ein Fehler aufgetreten",
  "error.body":
    "Die Ansicht konnte nicht dargestellt werden. Ihre Daten sind nicht verloren — der Vorgang ist gespeichert. Bitte laden Sie die Seite neu.",
  "error.details": "Technische Details",
  "error.reload": "Seite neu laden",
  "error.toOverview": "Zur Übersicht",

  // --- Ribbon ------------------------------------------------------------
  "ribbon.product": "Produkt",
  "ribbon.product.empty": "nicht gewählt",
  "ribbon.customer": "Kunde",
  "ribbon.customer.empty": "nicht geladen",
  "ribbon.documents": "Vertragspaket",
  "ribbon.documents.empty": "nicht erstellt",
  "ribbon.documents.value": "{count} Dokumente · {signable} zu signieren",
  "ribbon.signature": "Signatur",
  "ribbon.signature.empty": "ausstehend",
  "ribbon.signature.complete": "vollständig signiert",
  "ribbon.signature.partial": "{signed} von {total} signiert",
  "ribbon.signature.sent": "1 von {total} versendet",
  "ribbon.payment": "Zahlung",
  "ribbon.payment.empty": "offen",
  "ribbon.payment.paid": "bezahlt · {amount}",
  "ribbon.payment.linkSent": "Link versendet",
  "ribbon.payment.failed": "fehlgeschlagen",

  // --- Step rail ---------------------------------------------------------
  "rail.aria": "Schritte des Vorgangs",
  "rail.heading": "Vorgang",
  "rail.step1.title": "Produkt",
  "rail.step1.subtitle": "Was wird verkauft",
  "rail.step2.title": "Kundendaten",
  "rail.step2.subtitle": "Import oder CRM",
  "rail.step3.title": "Vertrag",
  "rail.step3.subtitle": "Erzeugen und prüfen",
  "rail.step4.title": "Signatur",
  "rail.step4.subtitle": "Zoho Sign",
  "rail.step5.title": "Zahlung",
  "rail.step5.subtitle": "LemonSqueezy",
  "rail.step6.title": "Freischaltung",
  "rail.step6.subtitle": "Zugang und Übergabe",

  // --- Deal / new deal ---------------------------------------------------
  "deal.readOnly":
    "Dieser Vorgang wird von {name} geführt. Sie können ihn ansehen, aber nicht bearbeiten.",
  "deal.loading": "Vorgang wird geladen …",
  "deal.notFound": "Vorgang nicht gefunden.",
  "deal.preparing": "Vorgang wird vorbereitet …",

  // --- Step 1: product ---------------------------------------------------
  "product.eyebrow": "Schritt 1 von 6",
  "product.title": "Welches Produkt verkaufen Sie?",
  "product.intro":
    "Jedes Produkt bringt sein eigenes Vertragspaket und Preismodell mit. Die weiteren Schritte passen sich der Auswahl an.",
  "product.locked":
    "Das Produkt kann nicht mehr gewechselt werden, weil die Signatur bereits angefordert wurde.",
  "product.loading": "Produkte werden geladen …",
  "product.emptyTitle": "Kein Produkt im Katalog.",
  "product.emptyBody":
    "Es sind keine Produkte hinterlegt, daher kann kein Vorgang gestartet werden. Das ist ein Konfigurationsproblem der Installation — bitte an die Administration wenden.",
  "product.demoHint": "Zeigen Sie dem Kunden im Gespräch, wie das Produkt im Alltag aussieht.",
  "product.openPreview": "Kundenvorschau öffnen",
  "product.chosen": "{name} gewählt.",
  "product.chooseHint": "Wählen Sie ein Produkt, um fortzufahren.",
  "product.continue": "Weiter zu Kundendaten",

  // --- Step 2: customer --------------------------------------------------
  "customer.eyebrow": "Schritt 2 von 6",
  "customer.title": "Kundendaten laden",
  "customer.intro":
    "Laden Sie eine CSV- oder Excel-Datei hoch und suchen Sie den passenden Kunden. Ein CRM ist nicht nötig — kann aber angebunden werden.",
  "customer.locked": "Die Kundendaten sind gesperrt, weil die Signatur bereits angefordert wurde.",
  "customer.attached": "ist diesem Vorgang zugeordnet.",
  "customer.complete": "Alle Pflichtangaben liegen vor.",
  "customer.dropTitle": "Datei hier ablegen",
  "customer.dropBody":
    "CSV oder Excel (.xlsx). Die Spalten werden automatisch erkannt — deutsche Kundenlisten (Firma, Straße, PLZ, Ort, Ansprechpartner, E-Mail) ebenso wie Lead-Exporte (Company Name, Company Address, City, First/Last Name, Email).",
  "customer.dropHint": "Fehlt die PLZ als eigene Spalte, wird sie aus der Adresse gelesen.",
  "customer.pickOther": "Andere Datei laden",
  "customer.pickFile": "Datei auswählen",
  "customer.discard": "Liste verwerfen",
  "customer.loaded": "Geladen:",
  "customer.rowCount.one": " · {count} Zeile",
  "customer.rowCount.other": " · {count} Zeilen",
  "customer.reading": " · wird gelesen …",
  "customer.uploadFailed": "Die Datei konnte nicht gelesen werden.",
  "customer.uploadFailedHint":
    "Die vorherige Liste wurde verworfen. Wählen Sie eine andere Datei, oder suchen Sie den Kunden unten im Bestand.",
  "customer.crmTitle": "CRM anbinden",
  "customer.crmSoon": "Demnächst",
  "customer.crmBody":
    "Die Anbindung ist in Vorbereitung. Sobald sie verfügbar ist, entfällt der Datei-Import — die Kundenliste kommt dann direkt aus dem System.",
  "customer.crmSoonAvailable": "Demnächst verfügbar",
  "customer.rowsTitle": "Zeilen aus der Datei",
  "customer.storedTitle": "Kunden im Bestand",
  "customer.rowsHint": "Wählen Sie die passende Zeile aus.",
  "customer.storedHint": "Oder laden Sie oben eine Datei, um neue Kunden zu übernehmen.",
  "customer.searchRows": "Importierte Zeilen durchsuchen",
  "customer.searchStored": "Bestandskunden suchen",
  "customer.searchPlaceholder": "Nach Firma, Ort, Ansprechpartner oder E-Mail suchen",
  "customer.noMatch": "Kein Treffer. Suchbegriff anpassen.",
  "customer.incompleteRow": "Unvollständige Zeile.",
  "customer.incompleteHint": "Ergänzen Sie die Angaben unten — sie werden für den Vertrag gebraucht.",
  "customer.field.street": "Straße und Hausnummer",
  "customer.field.email": "E-Mail für die Signatur",
  "customer.replace": "Kunden ersetzen",
  "customer.attach": "Kunden übernehmen",
  "customer.hint.none": "Kunde noch nicht gewählt.",
  "customer.hint.complete": "Alle Pflichtangaben vorhanden.",
  "customer.hint.incomplete": "Pflichtangaben unvollständig.",
  "customer.continue": "Weiter zum Vertrag",

  // --- Step 3: contract --------------------------------------------------
  "contract.eyebrow": "Schritt 3 von 6",
  "contract.title": "Vertragspaket erzeugen",
  "contract.intro":
    "Die Vorlagen sind einmal hinterlegt. Die Kundendaten werden automatisch eingesetzt — niemand bearbeitet ein Dokument von Hand.",
  "contract.generating": "Vertragspaket wird erzeugt …",
  "contract.signable": "signaturpflichtig",
  "contract.attachment": "Anlage",
  "contract.checkNowTitle": "Prüfen Sie jetzt.",
  "contract.checkNowBody":
    "Sobald der Kunde das Dokument in Zoho Sign öffnet, sind die eingesetzten Daten gesperrt und lassen sich nicht mehr ändern. Korrekturen danach bedeuten einen neuen Durchlauf.",
  "contract.expandHint": "Dokument anklicken, um den Inhalt mit den eingesetzten Daten zu sehen.",
  "contract.missingTitle": "Unvollständiges Paket.",
  "contract.missingBody":
    "Für diese signaturpflichtigen Dokumente fehlt die Vorlagendatei, sie gehen NICHT an den Kunden: {names}. Vor echten Vertragsabschlüssen nachreichen — der AVV ist nach Art. 28 DSGVO Pflicht.",
  "contract.loadingDetails": "Inhalt wird geladen …",
  "contract.noTemplate": "Vorlagendatei noch nicht hinterlegt",
  // The contract itself is a German legal document, so the text below stays
  // German in both languages; only this caption tells an English reader why.
  "contract.previewLanguageNote": "",
  "contract.hint": "Angaben stimmen? Dann geht das Paket in die Signatur.",
  "contract.continue": "Weiter zur Signatur",
  "contract.release": "Paket freigeben",

  // --- Step 4: signature -------------------------------------------------
  "signature.eyebrow": "Schritt 4 von 6",
  "signature.title": "Signatur über Zoho Sign",
  "signature.intro":
    "Zwei Unterschriften in fester Reihenfolge: erst der Kunde, dann die Geschäftsführung. Die zweite Einladung geht automatisch raus, sobald die erste erledigt ist.",
  "signature.status.waiting": "Wartet auf vorherigen Unterzeichner",
  "signature.status.sent": "Einladung versendet",
  "signature.status.signed": "Signiert",
  "signature.status.declined": "Abgelehnt",
  "signature.notSent": "Noch nicht versendet",
  "signature.completeTitle": "Vollständig signiert.",
  "signature.completeBody":
    "Das ausgefertigte Dokument liegt für beide Seiten zum Abruf bereit.",
  "signature.waitingFor":
    "Der Vorgang wartet auf {name}. Sobald Zoho Sign den Abschluss meldet, geht es automatisch weiter.",
  "signature.nextSigner": "den nächsten Unterzeichner",
  "signature.mockTitle": "Testmodus (SIGNATURE_PROVIDER=mock).",
  "signature.mockBody": "Es wird keine echte Einladung verschickt.",
  "signature.simulate": "Signatur von {name} simulieren",
  "signature.simulateEsign": "E-Signatur simulieren",
  "signature.graceTitle": "Noch nicht an Zoho Sign gesendet.",
  "signature.graceBody":
    "Jetzt simulieren, oder {seconds} s warten — dann geht die Einladung an Zoho Sign.",
  "signature.send": "Zur Signatur senden",
  "signature.running": "Signatur läuft.",
  "signature.continue": "Weiter zur Zahlung",
  "signature.placeholder.customer": "Kunde",
  "signature.placeholder.customerRole": "Kunde · Unterzeichner 1 von 2",
  "signature.placeholder.management": "Geschäftsführung",
  "signature.placeholder.managementRole": "Unterzeichner 2 von 2",

  // --- Step 5: payment ---------------------------------------------------
  "payment.eyebrow": "Schritt 5 von 6",
  "payment.title": "Zahlung über LemonSqueezy",
  "payment.intro":
    "Der Kunde erhält einen Zahlungslink. LemonSqueezy übernimmt die Abwicklung und die Umsatzsteuer.",
  "payment.lineItem": "Rechnungsposten",
  "payment.mode.one_time": "Einmalzahlung",
  "payment.mode.subscription": "Abonnement",
  "payment.mode.on_request": "Preis auf Anfrage",
  "payment.total": "Gesamt",
  "payment.paidTitle": "Zahlung eingegangen.",
  "payment.paidBody": "Der Beleg wurde automatisch an den Kunden versendet.",
  "payment.linkSent": "Zahlungslink versendet",
  "payment.linkSentAt": " am {when}",
  "payment.awaiting": ". Der Vorgang wartet auf den Zahlungseingang.",
  "payment.mockTitle": "Testmodus (PAYMENT_PROVIDER=mock).",
  "payment.mockBody": "Es wurde kein echter Checkout erstellt.",
  "payment.simulate": "Zahlungseingang simulieren",
  "payment.send": "Zahlungslink senden",
  "payment.waiting": "Warte auf Zahlung.",
  "payment.continue": "Weiter zur Freischaltung",

  // --- Step 6: activation ------------------------------------------------
  "activation.eyebrow": "Schritt 6 von 6",
  "activation.title": "Zugang freischalten",
  // Two whole sentences per state rather than one with an interpolated noun:
  // the German fallbacks are inflected and would not survive translation.
  "activation.done": "{product} ist für {customer} freigeschaltet.",
  "activation.doneNoCustomer": "{product} ist für den Kunden freigeschaltet.",
  "activation.todo": "Letzter Schritt: {product} wird für {customer} freigeschaltet.",
  "activation.todoNoCustomer": "Letzter Schritt: {product} wird für den Kunden freigeschaltet.",
  "activation.theProduct": "Das Produkt",
  "activation.showPreview": "Kundenvorschau zeigen",
  "activation.activate": "Zugang freischalten",
  "activation.completeTitle": "Vorgang abgeschlossen",
  "activation.completeBody": "Der Kunde hat seine Einrichtungs-E-Mail erhalten.",
  "activation.setupLink": "Einrichtungslink für den Kunden:",
  "activation.toOverview": "Zur Übersicht",
  "activation.newDeal": "Neuen Vorgang starten",

  // --- Customer preview overlay ------------------------------------------
  "preview.title": "{product} — Kundenvorschau",
  "preview.subtitle": "So sieht der Ablauf beim Kunden aus",
  "preview.scene": "Szene {index} von {total}",

  // --- Transport ---------------------------------------------------------
  "api.sessionExpired": "Sitzung abgelaufen. Bitte erneut anmelden.",
} as const;

export type MsgKey = keyof typeof de;

// The Record<MsgKey, string> annotation is the completeness check: tsc fails if
// a key is missing here or misspelled.
export const en: Record<MsgKey, string> = {
  "common.back": "Back",
  "common.next": "Continue",
  "common.close": "Close",
  "common.missing": "missing",

  "app.loading": "Loading the application …",
  "app.footerBrand": "AI2Connect Sales Platform",

  "lang.label": "Language",
  "lang.de": "DE",
  "lang.en": "EN",

  "topbar.subtitle": "Sales Platform",
  "topbar.nav": "Main navigation",
  "topbar.tab.overview": "Overview",
  "topbar.tab.deal": "Deal",
  "topbar.demo1": "Customer product demo 1",
  "topbar.demo2": "Customer product demo 2",
  "topbar.newTab": " (opens in a new tab)",
  "topbar.logout": "Sign out",

  "auth.shellNote": "AI2Connect Sales Platform · Sign in with your company account",

  "role.vertrieb": "Sales",
  "role.vertriebspartner": "Sales partner",
  "role.geschaeftsfuehrung": "Management",

  "field.company_name": "Company",
  "field.street": "Street",
  "field.postal_city": "Postcode and city",
  "field.postal_code": "Postcode",
  "field.city": "City",
  "field.contact_name": "Contact",
  "field.contact_email": "Email",
  "field.missingSentence": "Missing: {fields}.",

  "status.done": "Completed",
  "status.sig": "In signature",
  "status.open": "In progress",

  // --- Dashboard ---------------------------------------------------------
  "dashboard.greeting": "Hello, {name}",
  "dashboard.intro": "Every deal that has been run through the platform.",
  "dashboard.newDeal": "Start a new deal",
  "dashboard.createFailed": "The deal could not be created: {message}",
  "dashboard.filter.all": "All",
  "dashboard.filter.internal": "AI2Connect",
  "dashboard.filter.agency": "Sales agency",
  "dashboard.filter.mine": "Mine only",
  "dashboard.filter.open": "In progress",
  "dashboard.filter.done": "Completed",
  "dashboard.kpi.total": "Deals in total",
  "dashboard.kpi.totalHint": "{count} of them yours",
  "dashboard.kpi.open": "In progress",
  "dashboard.kpi.openHint": "not yet completed",
  "dashboard.kpi.completed": "Completed",
  "dashboard.kpi.completedHint": "contract signed and paid",
  "dashboard.kpi.volume": "Completed volume",
  "dashboard.kpi.volumeHint": "gross, paid deals only",
  "dashboard.search": "Search deals",
  "dashboard.searchPlaceholder": "Search by customer, product or deal number",
  "dashboard.col.reference": "Deal",
  "dashboard.col.customer": "Customer",
  "dashboard.col.product": "Product",
  "dashboard.col.owner": "Owner",
  "dashboard.col.created": "Created",
  "dashboard.col.value": "Value",
  "dashboard.col.status": "Status",
  "dashboard.loading": "Loading deals …",
  "dashboard.empty": "No deals found. Adjust your search or filter.",

  // --- Login -------------------------------------------------------------
  "login.signin.title": "Sign in",
  "login.signin.intro":
    "Every deal is recorded under your name, so it stays clear who ran it.",
  "login.signin.submit": "Sign in",
  "login.signup.title": "Register",
  "login.signup.intro":
    "Register with your AI2Connect address. You will receive an email with a link for setting your password.",
  "login.signup.submit": "Request registration",
  "login.forgot.title": "Forgotten password",
  "login.forgot.intro":
    "Enter your email address. We will send you a link for setting a new password.",
  "login.forgot.submit": "Request link",
  "login.error.email": "Please enter your email address.",
  "login.error.password": "Please enter your password.",
  "login.error.name": "Please enter your name.",
  "login.error.generic": "That did not work. Please try again later.",
  "login.sentHint": "The link can only be used once. Check your spam folder as well.",
  "login.backToSignin": "Back to sign in",
  "login.field.name": "First and last name",
  "login.field.role": "Role",
  "login.field.email": "Email",
  "login.field.password": "Password",
  "login.domainHint": "Only addresses on {domains} are permitted.",
  "login.partnerHint":
    "An address on {domains} lets you register as internal sales. Any other address is recorded as a sales partner.",
  "login.forcedPartner": "Addresses outside {domains} are recorded as sales partners.",
  "login.domainOr": " or ",
  "login.forgotLink": "Forgotten your password?",

  // --- Set password ------------------------------------------------------
  "setPassword.tooShort": "The password must be at least {count} characters long.",
  "setPassword.mismatch": "The passwords do not match.",
  "setPassword.failed": "The password could not be set. Please try again later.",
  "setPassword.incompleteTitle": "Incomplete link",
  "setPassword.incompleteBody":
    "The address is missing its token. Please open the link from the email unchanged.",
  "setPassword.checking": "Checking the link …",
  "setPassword.invalidTitle": "Invalid link",
  "setPassword.invalidBody":
    "This link is invalid, expired or has already been used. Request a new one via “Forgotten password”.",
  "setPassword.resetTitle": "New password",
  "setPassword.setTitle": "Set your password",
  "setPassword.resetIntro": "Set a new password for your account.",
  "setPassword.setIntro":
    "Welcome to AI2Connect. Set your password to activate your access.",
  "setPassword.account": "Account",
  "setPassword.field.new": "New password",
  "setPassword.field.confirm": "Confirm password",
  "setPassword.minHint": "At least {count} characters.",
  "setPassword.submit": "Save password and sign in",

  // --- Error boundary ----------------------------------------------------
  "error.title": "Something went wrong",
  "error.body":
    "This view could not be rendered. Your data is not lost — the deal is saved. Please reload the page.",
  "error.details": "Technical details",
  "error.reload": "Reload the page",
  "error.toOverview": "Back to overview",

  // --- Ribbon ------------------------------------------------------------
  "ribbon.product": "Product",
  "ribbon.product.empty": "not chosen",
  "ribbon.customer": "Customer",
  "ribbon.customer.empty": "not loaded",
  "ribbon.documents": "Contract package",
  "ribbon.documents.empty": "not created",
  "ribbon.documents.value": "{count} documents · {signable} to sign",
  "ribbon.signature": "Signature",
  "ribbon.signature.empty": "pending",
  "ribbon.signature.complete": "fully signed",
  "ribbon.signature.partial": "{signed} of {total} signed",
  "ribbon.signature.sent": "1 of {total} sent",
  "ribbon.payment": "Payment",
  "ribbon.payment.empty": "outstanding",
  "ribbon.payment.paid": "paid · {amount}",
  "ribbon.payment.linkSent": "link sent",
  "ribbon.payment.failed": "failed",

  // --- Step rail ---------------------------------------------------------
  "rail.aria": "Steps of the deal",
  "rail.heading": "Deal",
  "rail.step1.title": "Product",
  "rail.step1.subtitle": "What is being sold",
  "rail.step2.title": "Customer data",
  "rail.step2.subtitle": "Import or CRM",
  "rail.step3.title": "Contract",
  "rail.step3.subtitle": "Generate and check",
  "rail.step4.title": "Signature",
  "rail.step4.subtitle": "Zoho Sign",
  "rail.step5.title": "Payment",
  "rail.step5.subtitle": "LemonSqueezy",
  "rail.step6.title": "Activation",
  "rail.step6.subtitle": "Access and handover",

  // --- Deal / new deal ---------------------------------------------------
  "deal.readOnly": "{name} is running this deal. You can view it, but not change it.",
  "deal.loading": "Loading the deal …",
  "deal.notFound": "Deal not found.",
  "deal.preparing": "Preparing the deal …",

  // --- Step 1: product ---------------------------------------------------
  "product.eyebrow": "Step 1 of 6",
  "product.title": "Which product are you selling?",
  "product.intro":
    "Each product brings its own contract package and pricing model. The later steps adapt to your choice.",
  "product.locked": "The product can no longer be changed, as the signature has already been requested.",
  "product.loading": "Loading products …",
  "product.emptyTitle": "No product in the catalogue.",
  "product.emptyBody":
    "No products are configured, so no deal can be started. This is a configuration problem with the installation — please contact your administrator.",
  "product.demoHint": "Show the customer during the conversation what the product looks like day to day.",
  "product.openPreview": "Open customer preview",
  "product.chosen": "{name} chosen.",
  "product.chooseHint": "Choose a product to continue.",
  "product.continue": "Continue to customer data",

  // --- Step 2: customer --------------------------------------------------
  "customer.eyebrow": "Step 2 of 6",
  "customer.title": "Load customer data",
  "customer.intro":
    "Upload a CSV or Excel file and find the right customer. A CRM is not required — but one can be connected.",
  "customer.locked": "The customer data is locked, as the signature has already been requested.",
  "customer.attached": "is assigned to this deal.",
  "customer.complete": "All required details are present.",
  "customer.dropTitle": "Drop your file here",
  "customer.dropBody":
    "CSV or Excel (.xlsx). Columns are detected automatically — German customer lists (Firma, Straße, PLZ, Ort, Ansprechpartner, E-Mail) as well as lead exports (Company Name, Company Address, City, First/Last Name, Email).",
  "customer.dropHint": "If the postcode has no column of its own, it is read out of the address.",
  "customer.pickOther": "Load another file",
  "customer.pickFile": "Choose a file",
  "customer.discard": "Discard the list",
  "customer.loaded": "Loaded:",
  "customer.rowCount.one": " · {count} row",
  "customer.rowCount.other": " · {count} rows",
  "customer.reading": " · reading …",
  "customer.uploadFailed": "The file could not be read.",
  "customer.uploadFailedHint":
    "The previous list has been discarded. Choose a different file, or find the customer among the stored ones below.",
  "customer.crmTitle": "Connect a CRM",
  "customer.crmSoon": "Coming soon",
  "customer.crmBody":
    "The connection is being prepared. Once it is available, the file import becomes unnecessary — the customer list will come straight from the system.",
  "customer.crmSoonAvailable": "Available soon",
  "customer.rowsTitle": "Rows from the file",
  "customer.storedTitle": "Stored customers",
  "customer.rowsHint": "Pick the matching row.",
  "customer.storedHint": "Or load a file above to take on new customers.",
  "customer.searchRows": "Search the imported rows",
  "customer.searchStored": "Search stored customers",
  "customer.searchPlaceholder": "Search by company, city, contact or email",
  "customer.noMatch": "No match. Adjust your search term.",
  "customer.incompleteRow": "Incomplete row.",
  "customer.incompleteHint": "Fill in the details below — they are needed for the contract.",
  "customer.field.street": "Street and house number",
  "customer.field.email": "Email for the signature",
  "customer.replace": "Replace customer",
  "customer.attach": "Use this customer",
  "customer.hint.none": "No customer chosen yet.",
  "customer.hint.complete": "All required details present.",
  "customer.hint.incomplete": "Required details incomplete.",
  "customer.continue": "Continue to the contract",

  // --- Step 3: contract --------------------------------------------------
  "contract.eyebrow": "Step 3 of 6",
  "contract.title": "Generate the contract package",
  "contract.intro":
    "The templates are configured once. Customer data is inserted automatically — nobody edits a document by hand.",
  "contract.generating": "Generating the contract package …",
  "contract.signable": "signature required",
  "contract.attachment": "attachment",
  "contract.checkNowTitle": "Check it now.",
  "contract.checkNowBody":
    "As soon as the customer opens the document in Zoho Sign, the inserted data is locked and can no longer be changed. Corrections after that mean starting over.",
  "contract.expandHint": "Click a document to read it with the inserted data highlighted.",
  "contract.missingTitle": "Incomplete package.",
  "contract.missingBody":
    "These signature-bearing documents have no template file and will NOT reach the customer: {names}. Supply them before any real contract — the AVV is required by Art. 28 DSGVO.",
  "contract.loadingDetails": "Loading the content …",
  "contract.noTemplate": "no template file on record yet",
  "contract.previewLanguageNote":
    "The contract is a German legal document, so its text stays in German.",
  "contract.hint": "Details correct? Then the package goes into signature.",
  "contract.continue": "Continue to the signature",
  "contract.release": "Release the package",

  // --- Step 4: signature -------------------------------------------------
  "signature.eyebrow": "Step 4 of 6",
  "signature.title": "Signature via Zoho Sign",
  "signature.intro":
    "Two signatures in a fixed order: the customer first, then management. The second invitation goes out automatically once the first is done.",
  "signature.status.waiting": "Waiting for the previous signer",
  "signature.status.sent": "Invitation sent",
  "signature.status.signed": "Signed",
  "signature.status.declined": "Declined",
  "signature.notSent": "Not sent yet",
  "signature.completeTitle": "Fully signed.",
  "signature.completeBody": "The executed document is available to both parties.",
  "signature.waitingFor":
    "The deal is waiting for {name}. Once Zoho Sign reports completion, it continues automatically.",
  "signature.nextSigner": "the next signer",
  "signature.mockTitle": "Test mode (SIGNATURE_PROVIDER=mock).",
  "signature.mockBody": "No real invitation is sent.",
  "signature.simulate": "Simulate the signature by {name}",
  "signature.simulateEsign": "Simulate e-sign",
  "signature.graceTitle": "Not sent to Zoho Sign yet.",
  "signature.graceBody":
    "Simulate now, or wait {seconds}s — then the invitation goes to Zoho Sign.",
  "signature.send": "Send for signature",
  "signature.running": "Signature in progress.",
  "signature.continue": "Continue to the payment",
  "signature.placeholder.customer": "Customer",
  "signature.placeholder.customerRole": "Customer · signer 1 of 2",
  "signature.placeholder.management": "Management",
  "signature.placeholder.managementRole": "Signer 2 of 2",

  // --- Step 5: payment ---------------------------------------------------
  "payment.eyebrow": "Step 5 of 6",
  "payment.title": "Payment via LemonSqueezy",
  "payment.intro":
    "The customer receives a payment link. LemonSqueezy handles the processing and the VAT.",
  "payment.lineItem": "Invoice item",
  "payment.mode.one_time": "One-off payment",
  "payment.mode.subscription": "Subscription",
  "payment.mode.on_request": "Price on request",
  "payment.total": "Total",
  "payment.paidTitle": "Payment received.",
  "payment.paidBody": "The receipt has been sent to the customer automatically.",
  "payment.linkSent": "Payment link sent",
  "payment.linkSentAt": " on {when}",
  "payment.awaiting": ". The deal is waiting for the payment to arrive.",
  "payment.mockTitle": "Test mode (PAYMENT_PROVIDER=mock).",
  "payment.mockBody": "No real checkout was created.",
  "payment.simulate": "Simulate an incoming payment",
  "payment.send": "Send the payment link",
  "payment.waiting": "Waiting for payment.",
  "payment.continue": "Continue to the activation",

  // --- Step 6: activation ------------------------------------------------
  "activation.eyebrow": "Step 6 of 6",
  "activation.title": "Activate access",
  "activation.done": "{product} has been activated for {customer}.",
  "activation.doneNoCustomer": "{product} has been activated for the customer.",
  "activation.todo": "Final step: {product} is being activated for {customer}.",
  "activation.todoNoCustomer": "Final step: {product} is being activated for the customer.",
  "activation.theProduct": "The product",
  "activation.showPreview": "Show the customer preview",
  "activation.activate": "Activate access",
  "activation.completeTitle": "Deal completed",
  "activation.completeBody": "The customer has received their setup email.",
  "activation.setupLink": "Setup link for the customer:",
  "activation.toOverview": "Back to overview",
  "activation.newDeal": "Start a new deal",

  // --- Customer preview overlay ------------------------------------------
  "preview.title": "{product} — customer preview",
  "preview.subtitle": "This is how the process looks for the customer",
  "preview.scene": "Scene {index} of {total}",

  // --- Transport ---------------------------------------------------------
  "api.sessionExpired": "Your session has expired. Please sign in again.",
};
